import React, {Component, useMemo, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {
    AllCommunityModule,
    ColumnMenuModule,
    ColumnsToolPanelModule,
    ContextMenuModule,
    ServerSideRowModelModule,
    SetFilterModule,
} from "ag-grid-enterprise";
import {Helmet} from "react-helmet";
import {defaultZenGroupColumnInitWithOptionsWithValueGetter} from "../../utils/zenGroupDisplayNameGridHelper";

import {
    getColumnModeInSession,
    getDefaultAgGridSidebarProps,
    getUseColumnStateInSession,
    getUseFilterStateInSession,
    onColumnStateChangedHelper,
    onFilterChangedHelper,
    onGridReadyHelper,
    onGridReadyHelperForColumnState,
    updateColumnModeInSessionHelper,
    updateUseColumnStateHelper,
    updateUseFilterStateHelper
} from "../../utils/gridFilterStateAndColumnStateHelper";
import {ClearRefresh} from "../../components/clearRefreshButtons";
import CustomNameCellEditor from "../../utils/customCellEditor";
import DTPicker, {dateFilterParametersInHeaderSuppressSorting} from "../../utils/DTPicker";
import {GridColumnFilterStateSaving} from "../../components/columnfilterComponent";
import privatePageHeaderHelper from "../../utils/privatePageHeaderHelper";
import {standardExcelExportHelper} from "../../utils/excelExportHelper";
import {agentDisplayNameAndZenGroupDisplayNameOnlyCellEditingStopped} from "../../utils/gridCellEditing";
import {ClickToShowColumnOptionsWithToggleButtonGroup, customColumnModeText} from "../../components/clickToShowButtons";
import {masterFilterHelper, serverSideFilterHelperHandleMultiConditionChecks} from "../../utils/filterHelper";
import {ccUntrustedModuleLoadsListReactive} from "../api/relatedModuleLoadsApi";
import {defaultAgentNameColumnInitWithOptions} from "../../utils/agentNameGridHelper";
import {convertDllTrustError} from "./relatedModuleLoads";
import {dateValueFormatter} from "../../utils/gridDateFormatter";
import {DisplayOptions} from "../../components/displayOptionsComponent";
import {useOutletContext} from "react-router-dom";

let gridColumnStateSessionVariableName = "untrustedModuleLoadsGridColumnState"
let gridFilterStateSessionVariableName = "untrustedModuleLoadsGridFilterState"


export default function CcOnlyUntrustedModuleLoads() {
    const { isLoading, setIsLoading } = useOutletContext();
    const [gridApi, setGridApi] = useState(null);
    const [useFilterStateSettingToggled, setUseFilterStateSettingToggled] = useState(getUseFilterStateInSession(gridFilterStateSessionVariableName));
    const [useColumnStateSettingToggled, setUseColumnStateSettingToggled] = useState(getUseColumnStateInSession(gridColumnStateSessionVariableName));
    const [sseDataPullActive, setSSEDataPullActive] = useState(false);
    const [enableButtons, setEnableButtons] = useState(false);
    const [asyncTransactionWaitMillis, setAsyncTransactionWaitMillis] = useState(200); //200 to start for the initial sse data pull, will change when sse data pull is done for change streams
    const [columnMode, setColumnMode] = useState(getColumnModeInSession(gridColumnStateSessionVariableName));
    // eslint-disable-next-line no-unused-vars
    const [columnDefs, setColumnDefs] = useState([
        defaultZenGroupColumnInitWithOptionsWithValueGetter(false, true, true),
        defaultAgentNameColumnInitWithOptions(false, 5),
        { field: "path", headerName: "Path", initialWidth: 750,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ['contains', 'notContains', 'equals', 'startsWith', 'endsWith'],
                maxNumConditions: 2,
            },
            sortable: false
        },
        { field: "customTrustError", headerName: "Trust Error", initialWidth: 275,
            filter: 'agSetColumnFilter',
            filterParams: {
                buttons: ["reset", "apply", "cancel"],
                values: ['UNSIGNED', 'SIGNER_NOT_WHITELISTED', 'TAMPERED_IN_MEMORY', 'TAMPERED_ON_DISK', 'GENERIC_ERROR', 'INVALID_ERROR'],
                valueFormatter: function (params) {
                    if(params.value!=="(Select All)"){
                        return convertDllTrustError(params.value)
                    }
                    else{
                        return params.value;
                    }
                },
                suppressSorting: false,
                convertValuesToStrings: true
            },
            sortable: false,
            valueFormatter: function (params) {
                return convertDllTrustError(params.value)
            },
        },
        { field: "trustedForDataIncidents", headerName: "Trusted for Extortion Responses", initialWidth: 330,
            filter: 'agSetColumnFilter',
            filterParams: {
                buttons: ["reset", "apply", "cancel"],
                values: ['true', 'false'],
                valueFormatter: (params) => {
                    if(params.value!=="(Select All)"){
                        if(params.value === "true"){
                            return "Trusted"
                        }
                        else{
                            return "Untrusted"
                        }
                    }
                    else{
                        return params.value;
                    }
                },
                suppressSorting: true,
                convertValuesToStrings: true
            },
            valueFormatter: (params) => {
                if(params.node.data.trustedForDataIncidents){
                    return "Trusted"
                }
                else{
                    return "Untrusted"
                }
            },
            sortable: true
        },
        { field: "trustedForIdentityIncidents", headerName: "Trusted for Identity Accesses", initialWidth: 330,
            filter: 'agSetColumnFilter',
            filterParams: {
                buttons: ["reset", "apply", "cancel"],
                values: ['Trusted', 'Untrusted'],
                suppressSorting: true,
                convertValuesToStrings: true
            },
            valueGetter: (params) => {
                if(params.node.data.trustedForIdentityIncidents){
                    return "Trusted"
                }
                else{
                    return "Untrusted"
                }
            },
            sortable: true
        },
        { field: "dateCollected", headerName: "Collected", initialWidth: 280,
            filter: 'agDateColumnFilter',
            filterParams:dateFilterParametersInHeaderSuppressSorting(2),
            sortable: true,
            valueFormatter: dateValueFormatter
        },
    ])
    const [defaultColDef, setDefaultColDef] = useState(
        {
            resizable: true,
            filterParams: null,
            floatingFilter: true,
            headerClass: "border-0 border-b-0",
            cellClass: "outline:none",
            enableCellChangeFlash: true,
            autoHeight: false,
            cellDataType: false //disable inferring cell data type automatically, can be overridden in individual colDef
        }
    )

    const sideBar = useMemo(() => {
        //Inside useMemo to help prevent the sidebar from re-rendering
        return getDefaultAgGridSidebarProps()
    }, []);
    const rowSelection = useMemo(() => {
        return {
            mode: 'singleRow',
            enableClickSelection: true,
            checkboxes: false,
            headerCheckbox: false
        };
    }, []);


    return (
        <div className="flex flex-col h-full">
            <Helmet>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <title>CC Only Untrusted Module Loads</title>
                <script src="https://js.stripe.com/v3/"/>
                <link href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap" rel="stylesheet"/>
            </Helmet>
            <div className="flex flex-1 flex-col flex-nowrap gap-y-2 h-full">
                {privatePageHeaderHelper("CC Only Untrusted Module Loads")}
                <hr className="h-0.5" />
                <DisplayOptions useFilterStateSettingToggled={useFilterStateSettingToggled}
                                setUseFilterStateSettingToggled={setUseFilterStateSettingToggled}
                                toggleUpdateUseFilterState={toggleUpdateUseFilterState}
                                useColumnStateSettingToggled={useColumnStateSettingToggled}
                                setUseColumnStateSettingToggled={setUseColumnStateSettingToggled}
                                toggleUpdateUseColumnState={toggleUpdateUseColumnState} />
                <div className="flex flex-row justify-between gap-x-1 gap-y-3">
                    <div className={"self-end flex flex-col gap-y-3"}>
                    </div>
                    <div className={"flex flex-row flex-wrap gap-y-3 gap-x-8 self-end justify-end"}>
                        <ClickToShowColumnOptionsWithToggleButtonGroup
                            columnMode={columnMode} setColumnMode={setColumnMode} gridColumnStateSessionVariableName={gridColumnStateSessionVariableName} gridApi={gridApi}
                            disableMediumOption={true} disableMinOption={true} updateGridColumnModeFunction={null} />
                        <ClearRefresh gridApi = {gridApi} refreshGridFunction = {null} showRefreshIcon={false}/>

                    </div>
                </div>
                <div className="h-full flex flex-col gap-y-5" id="gridRoot">
                    {getGrid()}
                </div>
            </div>
        </div>
    );


    function toggleUpdateUseFilterState(toggleSetting){
        updateUseFilterStateHelper(toggleSetting, gridFilterStateSessionVariableName, null);
    }
    function toggleUpdateUseColumnState(toggleSetting){
        updateUseColumnStateHelper(toggleSetting, gridColumnStateSessionVariableName, null);
    }

    function getGrid(){
        return (
            <Grid
                columnDefs={columnDefs}
                sideBar={sideBar}
                rowSelection={rowSelection}
                defaultColDef={defaultColDef}
                setGridApi={setGridApi}
                setEnableButtons={setEnableButtons}
                columnMode={columnMode}
                setColumnMode={setColumnMode}
            />
        );
    }

    function excelExport(){
        standardExcelExportHelper(gridApi, sseDataPullActive, "untrustedModuleLoadsGridExport")
    }
}

function getDatasourceForGrid(localShowAllTelemetryToggled){
    return {
        getRows: async function (params) {
            try{
                let start = params.request.startRow;
                let end = params.request.endRow;
                let singleConditionFilters = masterFilterHelper(params.request.filterModel, true)
                let multiConditionFilters = serverSideFilterHelperHandleMultiConditionChecks(params.request.filterModel)
                let sortModel = params.request.sortModel;
                let sortModelForRequest = null;
                if(sortModel && sortModel.length > 0){
                    try{ //extra precaution for array access on the sort model so it does not break
                        if(sortModel[0].colId && sortModel[0].sort){
                            sortModelForRequest = sortModel[0]
                        }
                    }
                    catch(error){
                        console.log("Error generating sort request")
                    }
                }
                let count = end - start;
                let page = Math.floor(start / count)
                let objects = await ccUntrustedModuleLoadsListReactive(count, page, singleConditionFilters, multiConditionFilters, sortModelForRequest, localShowAllTelemetryToggled);
                let finalPage = true
                //This logic mimics how we would check in the rest api for what to send for the finalPage in the response. Same logic is being used here so we can just return a flux from the
                // endpoint instead of Mono with a Response object
                if(objects && objects.length === count){
                    finalPage = false
                }
                let lastIdx = undefined
                if (finalPage) {
                    lastIdx = (count * page) + objects.length
                }
                params.api.deselectAll();
                const success = (objects.length !== 0);
                if (success) {
                    params.api.hideOverlay() //clear any leftover no rows overlay
                    params.api.setGridOption("loading", false)
                    params.success({
                        rowData: objects, rowCount: lastIdx
                    })
                } else {
                    params.success({
                        rowData: objects, rowCount: lastIdx
                    }) //to stop loading circle from spinning and make it disappear, nothing in objects.
                    params.fail();
                }

            }
            catch(error){
                //console.log(error);
                params.api.showNoRowsOverlay();
                params.fail();
            }
        }
    }
}


class Grid extends Component {
    constructor(props, onClickRow, filterVals, setGridApi) {
        super(props);
    }
    onFirstDataRendered = (params) => {

    };
    onColumnStateChanged = (params) => {
        //function to handle when column state changes: sort change, column visibility changes, or a column position on grid is moved
        if(params.source !== "api" && params.source !== "gridOptionsChanged"){
            this.props.setColumnMode && this.props.setColumnMode(customColumnModeText)
            onColumnStateChangedHelper(params, gridColumnStateSessionVariableName, null) //TODO: Change to real endpoint to change col state
            updateColumnModeInSessionHelper(gridColumnStateSessionVariableName, null, customColumnModeText) //TODO: Change to real endpoint to change col state
        }
        else if(params.source === "api" && params.type === "sortChanged"){
            this.props.setColumnMode && this.props.setColumnMode(customColumnModeText)
            onColumnStateChangedHelper(params, gridColumnStateSessionVariableName, null) //TODO: Change to real endpoint to change col state
            updateColumnModeInSessionHelper(gridColumnStateSessionVariableName, null, customColumnModeText) //TODO: Change to real endpoint to change col state
        }
    }

    getContextMenuItems = (params) => {
        return [
            "resetColumns",
            "autoSizeAll"
        ];
    };

    onGridReady = async (gridReadyParams) => {
        this.gridApi = gridReadyParams.api;
        this.props.setGridApi(gridReadyParams.api);
        // Disable text selection on the page while holding shift or control (to allow grid selections to be done easily without selecting all text)
        ["keyup","keydown"].forEach((event) => {
            window.addEventListener(event, (e) => {
                document.onselectstart = function() {
                    return !(e.shiftKey || e.ctrlKey);
                }
            });
        });
        //Only max/custom are allowed for this grid
        let columnMode = this.props.columnMode
        //check which initial column mode to apply
        if(columnMode === customColumnModeText){
            onGridReadyHelperForColumnState(gridReadyParams, gridColumnStateSessionVariableName)
        }
        //else if columnMode is max then the default column state already shows the max amount of columns no need to update

        onGridReadyHelper(gridReadyParams, gridFilterStateSessionVariableName);
        let dataSource = getDatasourceForGrid(true) //only showing all telem
        gridReadyParams.api.setGridOption("serverSideDatasource", dataSource);
        //TODO: update endpoints
        /*await loadDataWithSSEAndStartChangeStreamListener("/getAIFirewallPolicies", null,
            this.populateGrid, null, gridReadyParams, this.props.setSSEDataPullActive, this.props.setAsyncTransactionWaitMillis, this.updateTransactionsToApply,
            this.abortController)*/
        //gridReadyParams.api.sizeColumnsToFit()
    };
    render() {
        return (
            <div className={"w-full h-full"} style={{minHeight: "400px"}}>
                <div id="myGrid" className="ag-theme-alpine rounded-md shadow h-full w-full">
                    <AgGridReact
                        columnMenu={"legacy"}
                        modules={[
                            ServerSideRowModelModule,
                            AllCommunityModule,
                            ColumnMenuModule,
                            ContextMenuModule,
                            ColumnsToolPanelModule,
                            SetFilterModule
                        ]}
                        defaultColDef={this.props.defaultColDef}
                        columnDefs={this.props.columnDefs}
                        getRowId={(params) => {
                            return params.data.id;
                        }}
                        suppressOverlays={["noRows"]}
                        suppressExcelExport={true}
                        suppressCsvExport={true}
                        suppressMultiSort={true}
                        components={{agDateInput: DTPicker, customNameCellEditor: CustomNameCellEditor}}
                        rowModelType={'serverSide'}
                        onGridReady={this.onGridReady}
                        onCellEditingStopped={agentDisplayNameAndZenGroupDisplayNameOnlyCellEditingStopped}
                        rowSelection={this.props.rowSelection}
                        onSelectionChanged={() => {
                            const selectedRows = this.gridApi.getSelectedRows();
                            this.props.onClickRow && this.props.onClickRow(selectedRows);
                        }}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                        maintainColumnOrder={true} //fixes issue where if you re-order/move column then click anywhere on the grid it reverts this change
                        onFirstDataRendered={this.onFirstDataRendered.bind(this)}
                        onFilterChanged={(params)=> {
                            //TODO: call to api
                            onFilterChangedHelper(params, gridFilterStateSessionVariableName, null);
                        }}
                        //columnState listeners
                        onSortChanged={this.onColumnStateChanged}
                        onColumnMoved={this.onColumnStateChanged}
                        onColumnVisible={this.onColumnStateChanged}
                        getContextMenuItems={this.getContextMenuItems}
                        sideBar={this.props.sideBar}
                    />
                </div>
            </div>
        );
    }
}
